/**
 * usePermissions — reactive hooks over the dynamic permission store.
 *
 * These read the backend-driven permission set (GET /api/rbac/my-permissions),
 * as opposed to `usePermission` (config/rbac.config.ts) which is the static,
 * role-based system. Prefer these for anything gated by a permission KEY.
 *
 *   const canManage = useCan('userMgmt.role.manage');
 *   const canAny    = useCan(['billing.bill.view', 'billing.invoice.manage']);
 *   const canAll    = useCanAll(['payroll.run.view', 'payroll.run.approve']);
 *
 * All fail OPEN until the permission set has loaded (see permissionSlice).
 */

import { usePermissionStore } from '@/store/slices/permissionSlice';

/** ANY-of reactive check. Re-renders when permissions change. */
export function useCan(perm: string | string[]): boolean {
  return usePermissionStore((s) => {
    if (!s.loaded) return true;
    if (s.isOwner) return true;
    const keys = Array.isArray(perm) ? perm : [perm];
    return keys.some((k) => s.permissions.has(k));
  });
}

/** ALL-of reactive check. */
export function useCanAll(perms: string | string[]): boolean {
  return usePermissionStore((s) => {
    if (!s.loaded) return true;
    if (s.isOwner) return true;
    const keys = Array.isArray(perms) ? perms : [perms];
    return keys.every((k) => s.permissions.has(k));
  });
}

export { usePermissionStore };
